import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { WebhookAction } from '@/types/quiz';

const schema = z.object({
  name: z.string().min(1, 'Action name is required'),
  webhookUrl: z.string().url('Please enter a valid webhook URL'),
  httpMethod: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
  includeSubmissionData: z.boolean(),
  includeUserEmail: z.boolean(),
  customPayload: z.string().optional().refine(
    (val) => {
      if (!val || val.trim() === '') return true;
      try {
        JSON.parse(val);
        return true;
      } catch {
        return false;
      }
    },
    { message: 'Invalid JSON format' }
  ),
});

type FormData = z.infer<typeof schema>;

interface WebhookActionFormProps {
  action?: WebhookAction;
  onSave: (action: Omit<WebhookAction, 'id'> & { id?: string }) => void;
  onCancel: () => void;
}

export function WebhookActionForm({ action, onSave, onCancel }: WebhookActionFormProps) {
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: action?.name || '',
      webhookUrl: action?.webhookUrl || '',
      httpMethod: action?.httpMethod || 'POST',
      includeSubmissionData: action?.includeSubmissionData || true,
      includeUserEmail: action?.includeUserEmail || true,
      customPayload: action?.customPayload || '',
    },
  });

  const includeSubmissionData = watch('includeSubmissionData');
  const includeUserEmail = watch('includeUserEmail');
  const httpMethod = watch('httpMethod');

  const onSubmit = (data: FormData) => {
    onSave({
      id: action?.id,
      type: 'webhook',
      name: data.name,
      webhookUrl: data.webhookUrl,
      httpMethod: data.httpMethod,
      includeSubmissionData: data.includeSubmissionData,
      includeUserEmail: data.includeUserEmail,
      customPayload: data.customPayload,
    });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(onSubmit)(e);
  };

  return (
    <form onSubmit={handleFormSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Action Name</Label>
        <Input
          id="name"
          placeholder="e.g., Send to CRM"
          {...register('name')}
        />
        {errors.name && (
          <p className="text-sm text-destructive">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="webhookUrl">Webhook URL</Label>
        <Input
          id="webhookUrl"
          type="url"
          placeholder="https://api.example.com/webhook"
          {...register('webhookUrl')}
        />
        {errors.webhookUrl && (
          <p className="text-sm text-destructive">{errors.webhookUrl.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="httpMethod">HTTP Method</Label>
        <Select value={httpMethod} onValueChange={(value) => setValue('httpMethod', value as any)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="GET">GET</SelectItem>
            <SelectItem value="POST">POST</SelectItem>
            <SelectItem value="PUT">PUT</SelectItem>
            <SelectItem value="PATCH">PATCH</SelectItem>
            <SelectItem value="DELETE">DELETE</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="includeSubmissionData"
            checked={includeSubmissionData}
            onCheckedChange={(checked) => setValue('includeSubmissionData', checked as boolean)}
          />
          <Label htmlFor="includeSubmissionData" className="cursor-pointer">
            Include quiz submission data
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="includeUserEmail"
            checked={includeUserEmail}
            onCheckedChange={(checked) => setValue('includeUserEmail', checked as boolean)}
          />
          <Label htmlFor="includeUserEmail" className="cursor-pointer">
            Include user email
          </Label>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="customPayload">Custom Payload (JSON)</Label>
        <Textarea
          id="customPayload"
          placeholder='{"custom_field": "value"}'
          rows={4}
          {...register('customPayload')}
        />
        {errors.customPayload && (
          <p className="text-sm text-destructive">{errors.customPayload.message}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Optional: Additional JSON data to send with the webhook
        </p>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {action ? 'Update Action' : 'Add Action'}
        </Button>
      </div>
    </form>
  );
}
